import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAvailableUnitTemplates,
  getPurchasedUnits,
  buyUnitToRoster,
  refundUnit,
  refundAllUnits,
  getLoadoutTemplates,
  applyLoadout,
  switchToCustom,
  createLoadoutTemplate,
  updateLoadoutTemplate,
  deleteLoadoutTemplate,
} from '@/api/shopApi';
import feedback from '@/components/ui/general/feedback/feedback';

/**
 * Loadout category constants (SSoT: backend is source of truth)
 * - random: Random units (has regenerate button)
 * - solo: Single unit type
 * - official: Mixed combos
 * - custom: Saved custom template
 * - custom_unsaved: Modified roster without saved template
 */
const LOADOUT_CATEGORIES = {
  RANDOM: 'random',
  SOLO: 'solo',
  OFFICIAL: 'official',
  CUSTOM: 'custom',
  CUSTOM_UNSAVED: 'custom_unsaved',
};

export function useShop(gameId) {
  const queryClient = useQueryClient();
  const [selectedUnitTemplate, setSelectedUnitTemplate] = useState(null);
  // Track which specific unit is being refunded (prevents race condition on rapid clicks)
  const [refundingUnitId, setRefundingUnitId] = useState(null);

  // Loadout state management (synced from backend via purchasedData.loadout_category)
  const [activeCategory, setActiveCategory] = useState(null);
  const [appliedLoadoutId, setAppliedLoadoutId] = useState(null);

  // Obtener los unit templates disponibles
  const { data: shopData, isLoading } = useQuery({
    queryKey: ['shop', gameId],
    queryFn: () => getAvailableUnitTemplates(gameId),
  });

  // Obtener las unidades compradas (roster)
  const { data: purchasedData, isLoading: isPurchasedLoading } = useQuery({
    queryKey: ['purchased', gameId],
    queryFn: () => getPurchasedUnits(gameId),
  });

  // Obtener los loadout templates disponibles
  const { data: loadoutData, isLoading: isLoadoutLoading } = useQuery({
    queryKey: ['loadout-templates', gameId],
    queryFn: () => getLoadoutTemplates(gameId),
  });

  // Mutación para comprar unidades
  // Backend clears preferred_loadout → category becomes custom_unsaved
  const buyMutation = useMutation({
    mutationFn: (unitTemplateId) => buyUnitToRoster(gameId, unitTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
      queryClient.invalidateQueries({ queryKey: ['shop', gameId] });
      queryClient.invalidateQueries({ queryKey: ['purchased', gameId] });
      setSelectedUnitTemplate(null);
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Mutación para reembolsar unidades
  // Backend clears preferred_loadout → category becomes custom_unsaved
  const refundMutation = useMutation({
    mutationFn: (unitId) => refundUnit(gameId, unitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
      queryClient.invalidateQueries({ queryKey: ['shop', gameId] });
      queryClient.invalidateQueries({ queryKey: ['purchased', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
    onSettled: () => {
      // Clear refunding state regardless of success/error
      setRefundingUnitId(null);
    },
  });

  // Mutación para reembolsar todas las unidades
  // Backend clears preferred_loadout → category becomes custom_unsaved
  const refundAllMutation = useMutation({
    mutationFn: () => refundAllUnits(gameId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
      queryClient.invalidateQueries({ queryKey: ['shop', gameId] });
      queryClient.invalidateQueries({ queryKey: ['purchased', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Mutación para aplicar loadout (plantilla de unidades)
  const loadoutMutation = useMutation({
    mutationFn: (loadoutTemplateId) => applyLoadout(gameId, loadoutTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
      queryClient.invalidateQueries({ queryKey: ['shop', gameId] });
      queryClient.invalidateQueries({ queryKey: ['purchased', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Mutación para cambiar a custom_unsaved (sin tocar unidades ni dinero)
  const switchToCustomMutation = useMutation({
    mutationFn: () => switchToCustom(gameId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchased', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Mutación para crear loadout template
  const createLoadoutMutation = useMutation({
    mutationFn: (data) => createLoadoutTemplate(gameId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadout-templates', gameId] });
      queryClient.invalidateQueries({ queryKey: ['purchased', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Mutación para actualizar loadout template
  const updateLoadoutMutation = useMutation({
    mutationFn: ({ loadoutTemplateId, data }) => updateLoadoutTemplate(gameId, loadoutTemplateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadout-templates', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  // Mutación para eliminar loadout template
  const deleteLoadoutMutation = useMutation({
    mutationFn: (loadoutTemplateId) => deleteLoadoutTemplate(gameId, loadoutTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loadout-templates', gameId] });
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    },
  });

  const handleSelectUnitTemplate = (unitTemplate) => {
    setSelectedUnitTemplate(unitTemplate);
  };

  const handleBuyUnit = (unitTemplateId) => {
    if (!unitTemplateId) {
      if (!selectedUnitTemplate) {
        feedback.warning('Select a unit first');
        return;
      }
      unitTemplateId = selectedUnitTemplate.unit_template.id;
    }
    buyMutation.mutate(unitTemplateId);
  };

  const handleRefundUnit = (unitId) => {
    // IMMEDIATE: Prevent double-click race condition
    // Set state synchronously before mutation starts
    if (refundingUnitId !== null) return;
    setRefundingUnitId(unitId);
    refundMutation.mutate(unitId);
  };

  const handleRefundAllUnits = () => {
    if (refundAllMutation.isPending) return;
    refundAllMutation.mutate();
  };

  /**
   * Aplica una plantilla de unidades (loadout)
   * @param {number|null} loadoutTemplateId - The loadout template ID (null to clear roster)
   */
  const handleApplyLoadout = (loadoutTemplateId) => {
    if (loadoutMutation.isPending) return;
    loadoutMutation.mutate(loadoutTemplateId);
  };

  /**
   * Clear roster (results in custom_unsaved with empty units)
   */
  const handleClearRoster = useCallback(() => {
    if (loadoutMutation.isPending) return;
    loadoutMutation.mutate(null);
  }, [loadoutMutation]);

  /**
   * Regenerate random loadout (re-apply the random template)
   */
  const handleRegenerateRandom = useCallback(() => {
    if (loadoutMutation.isPending) return;

    const randomTemplate = loadoutData?.loadout_templates?.find(t => t.category === 'random');
    if (randomTemplate) {
      handleApplyLoadout(randomTemplate.id);
    }
  }, [loadoutMutation.isPending, loadoutData, handleApplyLoadout]);

  /**
   * Switch to custom_unsaved without modifying units or money
   * Used when clicking Custom tab from another category
   */
  const handleSwitchToCustom = useCallback(() => {
    if (switchToCustomMutation.isPending) return;
    switchToCustomMutation.mutate();
  }, [switchToCustomMutation]);

  /**
   * Crea una nueva plantilla de loadout personalizada
   * @param {object} data - { name: string, units: [{unit_template_slug, quantity}] }
   */
  const handleCreateLoadout = (data) => {
    if (createLoadoutMutation.isPending) return;
    createLoadoutMutation.mutate(data);
  };

  /**
   * Actualiza una plantilla de loadout personalizada
   * @param {number} loadoutTemplateId - The loadout template ID
   * @param {object} data - { name?: string, units?: [{unit_template_slug, quantity}] }
   */
  const handleUpdateLoadout = (loadoutTemplateId, data) => {
    if (updateLoadoutMutation.isPending) return;
    updateLoadoutMutation.mutate({ loadoutTemplateId, data });
  };

  /**
   * Elimina una plantilla de loadout personalizada
   * @param {number} loadoutTemplateId - The loadout template ID
   */
  const handleDeleteLoadout = (loadoutTemplateId) => {
    if (deleteLoadoutMutation.isPending) return;
    deleteLoadoutMutation.mutate(loadoutTemplateId);
  };

  // Sync category from backend (SSoT: backend is source of truth)
  useEffect(() => {
    if (purchasedData) {
      // DEBUG: Log loadout category from backend
      console.log('[useShop] Syncing category from backend:', {
        loadout_category: purchasedData.loadout_category,
        loadout_template_id: purchasedData.loadout_template?.id,
        loadout_template_name: purchasedData.loadout_template?.name,
      });

      // Use loadout_category directly from backend
      setActiveCategory(purchasedData.loadout_category);
      setAppliedLoadoutId(purchasedData.loadout_template?.id ?? null);
    }
  }, [purchasedData]);

  return {
    // Data
    shopData,
    purchasedData,
    loadoutData,
    selectedUnitTemplate,

    // Loading states
    isLoading,
    isPurchasedLoading,
    isLoadoutLoading,

    // Selection
    handleSelectUnitTemplate,

    // Buy/Refund
    handleBuyUnit,
    handleRefundUnit,
    handleRefundAllUnits,
    isBuying: buyMutation.isPending,
    isRefunding: refundMutation.isPending,
    isRefundingAll: refundAllMutation.isPending,
    refundingUnitId,

    // Loadout templates
    handleApplyLoadout,
    handleCreateLoadout,
    handleUpdateLoadout,
    handleDeleteLoadout,
    isApplyingLoadout: loadoutMutation.isPending,
    isCreatingLoadout: createLoadoutMutation.isPending,
    isUpdatingLoadout: updateLoadoutMutation.isPending,
    isDeletingLoadout: deleteLoadoutMutation.isPending,
    maxCustomTemplates: loadoutData?.max_custom_templates,

    // Category management (SSoT from backend)
    activeCategory,
    appliedLoadoutId,
    handleClearRoster,
    handleRegenerateRandom,
    handleSwitchToCustom,
    isSwitchingToCustom: switchToCustomMutation.isPending,
    LOADOUT_CATEGORIES,
  };
}

// Export constants for use in other components
export { LOADOUT_CATEGORIES };